import { useRef, useState } from "react";
import { Sparkles, Send, User } from "lucide-react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AiTutor({ labId, taskContext }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm your Linux tutor. Stuck on a task? Ask me anything — I'll nudge you in the right direction without spoiling the answer." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  const scrollDown = () => {
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 30);
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    scrollDown();
    try {
      const res = await fetch(`${API}/tutor/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ lab_id: labId, message: msg, task_context: taskContext || "" }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
        scrollDown();
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "The tutor is unavailable right now. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
      scrollDown();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="font-display font-bold text-sm">AI Tutor</span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`tutor-msg-${m.role}`}>
            <div className={`h-7 w-7 rounded-md grid place-items-center shrink-0 ${m.role === "user" ? "bg-secondary" : "bg-primary text-primary-foreground"}`}>
              {m.role === "user" ? <User className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
            </div>
            <div className={`rounded-lg px-3 py-2 text-sm max-w-[85%] whitespace-pre-wrap fade-up ${m.role === "user" ? "bg-secondary" : "bg-accent/60 border border-border"}`}>
              {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 border-t border-border flex gap-2">
        <Input
          data-testid="tutor-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask the tutor…"
          disabled={streaming}
        />
        <Button size="icon" onClick={send} disabled={streaming} data-testid="tutor-send">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
